// Minimal Supabase client over plain fetch (AUTH-BACKEND.md).
//
// No SDK on purpose: the game needs auth, one table upsert and three edge
// functions, and a game bundle should not carry a database client for that.
//
// Guest-first is law. If the project is not configured, every call here reports
// `not_configured` and the game carries on offline — it never blocks play.
//
// Only the ANON key is ever present in the client. The service-role key stays
// server-side, managed via the machine's secrets manager (AUTH-BACKEND.md §3).

export interface SupabaseConfig {
  url: string;
  anonKey: string;
}

export function readConfig(): SupabaseConfig | null {
  const env = import.meta.env as Record<string, string | undefined>;
  const url = env.VITE_SUPABASE_URL;
  const anonKey = env.VITE_SUPABASE_ANON_KEY;
  if (!url || !anonKey) return null;
  return { url: url.replace(/\/$/, ''), anonKey };
}

export interface Session {
  accessToken: string;
  refreshToken: string;
  userId: string;
  email: string;
  expiresAt: number;
}

const SESSION_KEY = 'clayfarm.session';

/** PostgREST schema holding every Clay Farm table. Never bare `public`. */
const SCHEMA = 'clayfarm';

export function loadSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw) as Session;
    // An expired token is worse than none — it turns every call into a 401.
    return session.expiresAt > Date.now() ? session : null;
  } catch {
    return null;
  }
}

/**
 * The stored row regardless of expiry, so an expired ACCESS token can still be
 * revived through its refresh token instead of silently demoting the player to
 * guest after an hour away.
 */
function loadStoredSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw) as Session;
    return typeof session.refreshToken === 'string' && session.refreshToken.length > 0
      ? session
      : null;
  } catch {
    return null;
  }
}

// --- Refresh-token resilience ---------------------------------------------
//
// Cold boot with an expired access token used to mean a silent drop to guest.
// Now the stored refresh token gets one quiet shot at GoTrue:
//   * server accepts            → session restored, nothing surfaced
//   * server rejects (dead token) → session cleared, ONE calm status message,
//                                   the game carries on in guest/local mode
//   * network unreachable       → retry on the next visibilitychange, capped;
//                                 after the cap, same calm guest fallback
// Nothing here ever throws: `request()` already converts fetch failures into
// NetResults, and everything below runs fire-and-forget from the constructor.

/**
 * Surfaced exactly once per dead/expired refresh token. UI wiring note: the
 * account panel should pass this through `hud.setStatus(...)` — see main.ts
 * where the client is constructed (`new SupabaseClient()`), which is the only
 * call site that would need a one-line listener.
 */
export const SESSION_EXPIRED_MESSAGE =
  'Signed-in session ended — playing as guest. Sign in again anytime.';

export type SessionStatus = { kind: 'refreshed' } | { kind: 'expired'; message: string };

/** Visibility-change retries before giving up and settling into guest mode. */
const MAX_REFRESH_ATTEMPTS = 3;

interface RefreshResponse {
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
  user?: { id?: string; email?: string };
}

export function saveSession(session: Session | null): void {
  try {
    if (session) localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    else localStorage.removeItem(SESSION_KEY);
  } catch {
    // Private browsing with storage disabled: the player stays a guest.
  }
}

export type NetResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: 'not_configured' | 'offline' | 'unauthorized' | 'server'; detail?: string };

async function request<T>(
  config: SupabaseConfig,
  path: string,
  init: RequestInit & { accessToken?: string } = {},
): Promise<NetResult<T>> {
  const { accessToken, ...rest } = init;
  try {
    const response = await fetch(`${config.url}${path}`, {
      ...rest,
      headers: {
        apikey: config.anonKey,
        authorization: `Bearer ${accessToken ?? config.anonKey}`,
        'content-type': 'application/json',
        // The Supabase instance is shared, so Clay Farm's tables live in their
        // own schema; PostgREST needs to be told which one on every request.
        'accept-profile': SCHEMA,
        'content-profile': SCHEMA,
        ...(rest.headers ?? {}),
      },
    });
    if (response.status === 401 || response.status === 403) {
      return { ok: false, error: 'unauthorized' };
    }
    if (!response.ok) {
      return { ok: false, error: 'server', detail: `${response.status}` };
    }
    const text = await response.text();
    return { ok: true, data: (text ? JSON.parse(text) : null) as T };
  } catch (err) {
    // Any network failure is survivable: the game is playable offline.
    return { ok: false, error: 'offline', detail: String(err) };
  }
}

export class SupabaseClient {
  private session: Session | null;
  /** Stored-but-expired session waiting for a refresh attempt. */
  private pendingRefresh: Session | null = null;
  private refreshAttempts = 0;
  private refreshInFlight: Promise<void> | null = null;
  private expiredSurfaced = false;
  private visibilityArmed = false;
  private statusListeners = new Set<(status: SessionStatus) => void>();

  constructor(readonly config: SupabaseConfig | null = readConfig()) {
    this.session = loadSession();
    // No live session, but maybe a refreshable one. Fire-and-forget: a dead
    // token must never turn a cold boot into a thrown error.
    if (!this.session && this.config && accountsEnabled()) {
      const stored = loadStoredSession();
      if (stored) {
        this.pendingRefresh = stored;
        void this.tryRefresh();
      }
    }
  }

  /**
   * Observe session lifecycle (restore / calm expiry). Returns an unsubscriber.
   * Listeners must not throw; a throwing listener is skipped, not fatal.
   */
  onSessionStatus(listener: (status: SessionStatus) => void): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  private emitStatus(status: SessionStatus): void {
    for (const listener of this.statusListeners) {
      try {
        listener(status);
      } catch {
        // A broken UI listener is never a reason to break the session flow.
      }
    }
  }

  /** True when an expired session is parked awaiting a refresh attempt. */
  get refreshPending(): boolean {
    return this.pendingRefresh !== null;
  }

  /**
   * One guarded refresh attempt. Public so tests (and a future UI "check my
   * session" affordance) can drive it without reaching into internals; also
   * what the visibilitychange listener calls.
   */
  async tryRefresh(): Promise<boolean> {
    if (!this.config || !this.pendingRefresh || this.refreshInFlight) return false;
    this.refreshInFlight = this.doRefresh();
    try {
      await this.refreshInFlight;
    } finally {
      this.refreshInFlight = null;
    }
    return this.session !== null;
  }

  private async doRefresh(): Promise<void> {
    const stored = this.pendingRefresh;
    if (!this.config || !stored) return;

    let result: NetResult<RefreshResponse>;
    try {
      result = await request<RefreshResponse>(this.config, '/auth/v1/token?grant_type=refresh_token', {
        method: 'POST',
        body: JSON.stringify({ refresh_token: stored.refreshToken }),
      });
    } catch (err) {
      // Belt and braces — `request` already catches everything, but a throw
      // escaping onto the console at boot is exactly what this file forbids.
      result = { ok: false, error: 'offline', detail: String(err) };
    }

    if (result.ok && result.data?.access_token && result.data.user?.id) {
      const claims = decodeJwtClaims(result.data.access_token);
      const session: Session = {
        accessToken: result.data.access_token,
        // GoTrue rotates the refresh token on use; keep whichever it gave us.
        refreshToken: result.data.refresh_token ?? stored.refreshToken,
        userId: result.data.user.id,
        email: result.data.user.email ?? claims?.email ?? stored.email,
        expiresAt: Date.now() + (result.data.expires_in ?? 3600) * 1000,
      };
      this.session = session;
      this.pendingRefresh = null;
      this.refreshAttempts = 0;
      this.expiredSurfaced = false;
      saveSession(session);
      this.emitStatus({ kind: 'refreshed' });
      return;
    }

    // The auth server ANSWERED and said no: the refresh token itself is dead
    // (revoked, reused, or user deleted). Retrying cannot help.
    const rejectedByServer =
      !result.ok &&
      (result.error === 'unauthorized' ||
        (result.error === 'server' && (result.detail === '400' || result.detail === '403')));

    if (rejectedByServer) {
      this.settleAsExpired();
      return;
    }

    // Unreachable network (or a transient 5xx): worth another try later.
    this.refreshAttempts += 1;
    if (this.refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
      this.settleAsExpired();
      return;
    }
    this.armVisibilityRetry();
  }

  /**
   * The calm ending: clear credentials, become guest/local, tell whoever is
   * listening exactly once. Never throws, never logs an error.
   */
  private settleAsExpired(): void {
    this.pendingRefresh = null;
    this.session = null;
    saveSession(null);
    if (!this.expiredSurfaced) {
      this.expiredSurfaced = true;
      this.emitStatus({ kind: 'expired', message: SESSION_EXPIRED_MESSAGE });
    }
  }

  private armVisibilityRetry(): void {
    if (typeof document === 'undefined' || this.visibilityArmed) return;
    this.visibilityArmed = true;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void this.tryRefresh();
    });
  }

  get configured(): boolean {
    return this.config !== null;
  }

  get signedIn(): boolean {
    return this.session !== null;
  }

  get userId(): string | null {
    return this.session?.userId ?? null;
  }

  get email(): string | null {
    return this.session?.email ?? null;
  }

  signOut(): void {
    this.session = null;
    saveSession(null);
  }

  /**
   * Sends the magic link. The address is the PARENT's — the UI must frame it
   * that way, and it doubles as the COPPA verifiable parental contact.
   */
  async sendMagicLink(email: string, locale: string): Promise<NetResult<null>> {
    if (!this.config) return { ok: false, error: 'not_configured' };
    return request<null>(this.config, '/auth/v1/otp', {
      method: 'POST',
      body: JSON.stringify({
        email,
        create_user: true,
        data: { locale },
      }),
    });
  }

  /** Completes login from the tokens the magic link puts in the URL fragment. */
  adoptSessionFromUrl(hash = window.location.hash): Session | null {
    const params = new URLSearchParams(hash.replace(/^#/, ''));
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    const expiresIn = Number(params.get('expires_in') ?? 3600);
    if (!accessToken || !refreshToken) return null;

    const claims = decodeJwtClaims(accessToken);
    if (!claims?.sub) return null;
    const session: Session = {
      accessToken,
      refreshToken,
      userId: claims.sub,
      email: claims.email ?? '',
      expiresAt: Date.now() + expiresIn * 1000,
    };
    this.session = session;
    this.expiredSurfaced = false;
    saveSession(session);
    return session;
  }

  async upsertSave(saveJson: string): Promise<NetResult<null>> {
    if (!this.config) return { ok: false, error: 'not_configured' };
    if (!this.session) return { ok: false, error: 'unauthorized' };
    return request<null>(this.config, '/rest/v1/saves?on_conflict=user_id', {
      method: 'POST',
      accessToken: this.session.accessToken,
      headers: { prefer: 'resolution=merge-duplicates,return=minimal' },
      body: JSON.stringify({
        user_id: this.session.userId,
        save_json: saveJson,
        updated_at: new Date().toISOString(),
      }),
    });
  }

  async fetchSave(): Promise<NetResult<{ save_json: string; updated_at: string } | null>> {
    if (!this.config) return { ok: false, error: 'not_configured' };
    if (!this.session) return { ok: false, error: 'unauthorized' };
    const result = await request<Array<{ save_json: string; updated_at: string }>>(
      this.config,
      `/rest/v1/saves?user_id=eq.${this.session.userId}&select=save_json,updated_at`,
      { accessToken: this.session.accessToken },
    );
    if (!result.ok) return result;
    return { ok: true, data: result.data[0] ?? null };
  }

  /** COPPA deletion right — the parent asks, everything goes. */
  async deleteAccountData(): Promise<NetResult<null>> {
    if (!this.config) return { ok: false, error: 'not_configured' };
    if (!this.session) return { ok: false, error: 'unauthorized' };
    return this.callFunction<null>('clayfarm-delete-account', {});
  }

  async callFunction<T>(name: string, body: unknown): Promise<NetResult<T>> {
    if (!this.config) return { ok: false, error: 'not_configured' };
    return request<T>(this.config, `/functions/v1/${name}`, {
      method: 'POST',
      accessToken: this.session?.accessToken,
      body: JSON.stringify(body),
    });
  }
}

interface JwtClaims {
  sub?: string;
  email?: string;
}

/** Reads the claims for the user id only — never used to trust anything. */
export function decodeJwtClaims(token: string): JwtClaims | null {
  try {
    const payload = token.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as JwtClaims;
  } catch {
    return null;
  }
}

/**
 * The accounts kill switch, and it fails CLOSED.
 *
 * Only `1`/`true` turns accounts on. An absent variable used to mean "on",
 * which made a build that lost its env file — or that followed `.env.example`,
 * which never documented the flag — ship account creation and deletion to a
 * child audience while reporting itself disabled. A safety switch that fails
 * open is worse than no switch, so the only way to get this feature is to
 * type it on purpose.
 *
 * See docs/ACCOUNTS-READINESS.md §2. Enabling it also needs counsel: there is
 * no consent flow, so it must stay off until there is one.
 */
export function accountsEnabled(): boolean {
  const raw = (import.meta.env as Record<string, string | undefined>).VITE_ENABLE_ACCOUNTS;
  if (raw === undefined) return false;
  return raw.trim().toLowerCase() === '1' || raw.trim().toLowerCase() === 'true';
}
